interface SparklineProps {
  values: (number | null)[];
  color: string;
  height?: number;
}

/** Lightweight inline sparkline (SVG polyline + end dot). No chart lib — the
 * per-metric trend cards don't need one, and this keeps the bundle lean. */
export function Sparkline({ values, color, height = 40 }: SparklineProps) {
  const nums = values.map((v) => (typeof v === 'number' ? v : null));
  const present = nums.filter((v): v is number => v !== null);
  if (present.length === 0) {
    return (
      <div style={{ height }} className="flex items-center text-micro text-fg-subtle">
        —
      </div>
    );
  }
  if (present.length === 1) {
    return (
      <svg
        viewBox="0 0 100 30"
        preserveAspectRatio="none"
        style={{ width: '100%', height }}
        aria-hidden
      >
        <circle cx={50} cy={15} r={2.5} fill={color} vectorEffect="non-scaling-stroke" />
      </svg>
    );
  }

  const min = Math.min(...present);
  const max = Math.max(...present);
  const span = max - min || 1;
  const W = 100;
  const H = 30;
  const step = nums.length > 1 ? W / (nums.length - 1) : W;

  let d = '';
  let lastX = 0;
  let lastY = 0;
  nums.forEach((v, i) => {
    if (v === null) return;
    const x = i * step;
    const y = H - ((v - min) / span) * H;
    d += `${d ? ' L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
    lastX = x;
    lastY = y;
  });

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height }}
      aria-hidden
    >
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lastX} cy={lastY} r={2.5} fill={color} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
